/**
 * 卸コンソール（app.kenzai-order.potential-x.com・D-26）。
 *
 * 画面は 05 §6.1 の画面IDと 1:1（D-43）＝サイドバー IA は Shell.tsx。
 * M1 で作るのは **C-01〜C-04・C-09/C-10/C-13・C-16**（C-05〜C-08・C-11/C-12/C-14 は M2/M3）。
 */

import { auth, type ConsoleMe } from "@kenzai/api-client";
import { AuthCard } from "@kenzai/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { Login } from "./Login";
import { PasswordForgot } from "./PasswordForgot";
import { PasswordSetup } from "./PasswordSetup";
import { Shell } from "./Shell";
import { Bins } from "./pages/Bins";
import { Staff } from "./pages/Staff";
import { Dashboard } from "./pages/Dashboard";
import { Imports } from "./pages/Imports";
import { Orders } from "./pages/Inbox";
import { CustomerDetail } from "./pages/CustomerDetail";
import { Customers, Products } from "./pages/Masters";
import { OrderDetail } from "./pages/OrderDetail";
import { OrderNew } from "./pages/OrderNew";
import { Requests } from "./pages/Requests";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

type State = { kind: "loading" } | { kind: "anon" } | { kind: "authed"; me: ConsoleMe };

export function App() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const isSetup = window.location.pathname.startsWith("/password/setup");
  // 再設定の申請（FR-112①）も**未ログインで開く**＝/console/me を取りに行かない
  // （401 が出るだけで害は無いが、無駄な往復で表示が1拍遅れる）。
  const isForgot = window.location.pathname.startsWith("/password/forgot");

  useEffect(() => {
    if (isSetup || isForgot) return;
    // 起動時に主体を取りに行く（05 §4.1 の /console/me）。401 は「未ログイン」＝正常系。
    auth
      .me()
      .then((me) => setState({ kind: "authed", me }))
      .catch(() => setState({ kind: "anon" }));
  }, [isSetup, isForgot]);

  // 初回パスワード設定（ワンタイムURL）＝未ログインで開く画面（FR-111）
  if (isSetup) return <PasswordSetup />;
  if (isForgot) return <PasswordForgot />;
  if (state.kind === "loading") return <AuthCard title="読み込み中…">{null}</AuthCard>;
  if (state.kind === "anon") return <Login onSuccess={(me) => setState({ kind: "authed", me })} />;

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Shell me={state.me} onLogout={() => setState({ kind: "anon" })} />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {/* 旧 C-02 受注箱＝**C-04 受注管理に統合**（D-76）。ブックマーク・ヘルプ記事・
                LINE の案内に残った旧 URL を **404 でも catch-all のダッシュボードでもなく**
                「未処理」タブへ連れていく（黙って無関係な画面に着地させない）。 */}
            <Route path="/inbox" element={<Navigate to="/orders?tab=todo" replace />} />
            <Route path="/orders" element={<Orders />} />
            {/* C-17 手入力（T-D7）。**`/orders/:orderId` より前**＝"new" が id として食われないように */}
            <Route path="/orders/new" element={<OrderNew />} />
            <Route path="/orders/:orderId" element={<OrderDetail />} />
            {/* C-21 指示・依頼（T-D40・D-69）＝社内指示＋運営への発注代行依頼 */}
            <Route path="/requests" element={<Requests me={state.me} />} />
            <Route path="/masters/customers" element={<Customers />} />
            {/* C-09 得意先の単票（現場・個別単価・招待コード導線＝T-D8） */}
            <Route path="/masters/customers/:customerId" element={<CustomerDetail />} />
            <Route path="/masters/products" element={<Products />} />
            <Route path="/masters/bins" element={<Bins />} />
            {/* D-79：C-19 は C-10 のタブへ／C-13・C-20 は C-09 の下へ。
                旧 URL は**それぞれ正しいタブ**に連れていく（catch-all のダッシュボードに
                黙って落とさない＝ブックマーク・ヘルプ記事・過去の案内文が死なない）。 */}
            <Route path="/masters/catalogs" element={<Navigate to="/masters/products?tab=listing" replace />} />
            <Route path="/staff" element={<Staff />} />
            <Route
              path="/invite-codes"
              element={<Navigate to="/masters/customers?tab=invite-codes" replace />}
            />
            {/* C-20 接続（アプリ利用者）＝T-D24。D-79 で取引先の下へ移した */}
            <Route path="/connections" element={<Navigate to="/masters/customers?tab=connections" replace />} />
            <Route path="/imports" element={<Imports />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
